import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ModalConfirm } from "@talentbait/shared-components";

import Actions from "../../../Actions";
import { TrashIcon } from "../../../Components/Icons";
import { ALL_STATUSES } from "../../Dashboard/DashboardStore";
import { getStatusInfos, renderStatus } from "../../../utils/Helpers";
import { Tooltip } from "../../../Components/Overlays";
import { badgeStatus } from "../../Dashboard/DashboardStatuses";
import { StatusBadge } from "../dashboard/AdAssistantDashboard.styled";
import { Spinner } from "../../../Components/Upload/Upload.styled";
import moment from "moment";

export const useAdAssistantDashboard = (organization, userProfile) => {
  const { t } = useTranslation();
  const [campaigns, setCampaigns] = useState([]);
  const [removingCampaignId, setRemovingCampaignId] = useState(null);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(true);

  const removeCampaignById = useCallback((campaignId) => {
    setCampaigns((prev) => prev.filter((campaign) => campaign.id !== campaignId));
  }, []);

  const fetchCampaigns = useCallback(() => {
    if (organization?.id) {
      Actions.loadFilteredCampaigns({
        organizationId: organization?.id,
        filters: { statuses: [...ALL_STATUSES] }
      });

      const loadFilteredCampaignsRef = Actions.loadFilteredCampaigns.completed.listen(
        ({ payload: campaignList }) => {
          const assistantFilteredCampaigns = campaignList.filter((campaign) => {
            return campaign.type === "assistant";
          });

          setIsLoadingCampaigns(false);
          setRemovingCampaignId(null);
          setCampaigns(assistantFilteredCampaigns);
          loadFilteredCampaignsRef();
        }
      );

      const loadFilteredCampaignsFailedRef = Actions.loadFilteredCampaigns.failed.listen(
        (error) => {
          setIsLoadingCampaigns(false);
          setRemovingCampaignId(null);
          loadFilteredCampaignsFailedRef();
        }
      );
    }
  }, [organization?.id]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleDeleteDraft = (campaignId, orgaId, campaignData) => {
    const { jobTitle, companyName } = campaignData;
    const { userName = "" } = userProfile;
    const { name: orgaName = "" } = organization;

    setRemovingCampaignId(campaignId);

    Actions.deleteDraftCampaign(campaignId, orgaId, removeCampaignById);
    Actions.toggleModal("showGenericModal", false);
    Actions.notifiyEvent({
      eventName: "entwurf",
      text: `📋🗑 Draft campaign deleted for *${jobTitle}*/${companyName} by *${userName}* (${orgaName}) `
    });

    fetchCampaigns();
  };

  const nameToSlug = (name) => {
    return (name || "").toLowerCase().replace(/\s/g, "-");
  };

  const renderDeleteDraft = (campaignId, orgaId, campaignData) => {
    return (
      <>
        {removingCampaignId === campaignId ? (
          <Spinner />
        ) : (
          <TrashIcon
            id={nameToSlug(campaignData.campaignName)}
            styling={{ marginLeft: "5px" }}
            onClick={() => {
              Actions.toggleModal("showGenericModal", true, {
                headline: t("Delete draft"),
                widthVersion: "small",
                view: (
                  <ModalConfirm
                    showDescription={true}
                    subtitle={campaignData.campaignName}
                    labelConfirm={t("Yes, delete")}
                    description={t("Are you sure you want to delete this draft campaign?")}
                    cancelledAction={() => {
                      Actions.toggleModal("showGenericModal", false);
                    }}
                    confirmedAction={() => handleDeleteDraft(campaignId, orgaId, campaignData)}
                  />
                )
              });
            }}
          />
        )}
      </>
    );
  };

  const renderStatusBadge = (status) => {
    let text = "";
    if (status == "ACTION_REQUIRED") {
      text = t("In progress");
    }
    text = renderStatus(status, t);

    return (
      <Tooltip
        label={getStatusInfos(status, t)}
        color={badgeStatus[status].color}
        bgColor={badgeStatus[status].background}
        enabled={getStatusInfos(status, t) !== ""}
      >
        <StatusBadge status={status}>{text}</StatusBadge>
      </Tooltip>
    );
  };

  const renderDateCell = (createdDate) => {
    return (
      <span>
        <span>
          {moment(createdDate, "YYYY-MM-DD").format("DD.MM.YYYY")} <br />
        </span>
      </span>
    );
  };

  return {
    campaigns,
    isLoadingCampaigns,
    renderDeleteDraft,
    renderStatusBadge,
    renderDateCell,
    removeCampaignById
  };
};
